import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Globe2, CheckCircle2, Cpu } from 'lucide-react';

const pillars = [
  {
    icon: Users,
    title: '100% In-House Engineering Team',
    desc: 'We never subcontract code or use outsourced labor. Every system is built and maintained by our dedicated in-house engineers.',
    accent: '#0284C7',
  },
  {
    icon: Shield,
    title: 'Fixed-Scope & Transparent Quotes',
    desc: 'Zero surprise hourly fees or hidden charges. We audit your requirements and provide fixed milestone proposals upfront.',
    accent: '#7C3AED',
  },
  {
    icon: Globe2,
    title: 'Global Scale & North American Focus',
    desc: 'Engineering resilient cloud applications, AI pipelines, and custom software for enterprises and scaling startups across the US, Canada, and worldwide.',
    accent: '#059669',
  },
];

const About = () => {
  return (
    <section id="about" className="py-24 bg-[#FFFFFF] text-slate-900 relative overflow-hidden">
      {/* Very subtle grid dot pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.025]" style={{
        backgroundImage: 'radial-gradient(circle, #0f172a 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="section-label mb-3">Enterprise Software House</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">
              Custom software systems built for{' '}
              <span className="text-sky-600">long-term reliability.</span>
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed font-normal">
              Zynqora Edge is a full-service software engineering agency. Directed by Haris (Chief Technology Officer), our engineering team bridges deep technical architecture with intuitive user design — transforming operational bottlenecks into automated cloud infrastructure for modern companies.
            </p>
          </motion.div>
        </div>

        {/* Core Pillars Bento Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {pillars.map((p, idx) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: idx * 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="bento-card-light p-8 flex flex-col justify-between group"
            >
              <div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 transition-all duration-300"
                  style={{
                    backgroundColor: `${p.accent}12`,
                    border: `1px solid ${p.accent}25`,
                    color: p.accent,
                  }}
                >
                  <p.icon size={22} />
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-3 group-hover:text-sky-600 transition-colors duration-200">
                  {p.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed font-normal">{p.desc}</p>
              </div>

              <div className="mt-8 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-mono text-slate-500">
                <CheckCircle2 size={14} className="text-emerald-500" />
                <span>Audited Standard</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Engineering Philosophy Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Cpu size={22} />
            </div>
            <div>
              <h4 className="font-heading font-bold text-base text-white">Technical Leadership &amp; Code Quality</h4>
              <p className="text-sm text-slate-400 mt-1 max-w-xl">
                Under CTO Haris' guidance, every pull request undergoes strict peer review, automated testing, and security checks before hitting production.
              </p>
            </div>
          </div>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="whitespace-nowrap px-6 py-3 rounded-xl bg-white text-slate-900 font-semibold text-sm tracking-tight hover:bg-slate-100 transition-colors inline-flex items-center gap-2 shadow-lg flex-shrink-0"
          >
            Work With Our Engineers
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default About;
